import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, User as UserIcon } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 80);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#F4EFE7]/85 backdrop-blur-md border-b border-[#1E1B18]/5' : 'bg-transparent'}`}>
      <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-4 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-3" aria-label="Aura Lune home">
          <Logo size={48} />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollTo('services')} className="text-[13px] text-[#1E1B18]/80 hover:text-[#1E1B18] transition-colors font-medium">Services</button>
          <button onClick={() => scrollTo('therapist')} className="text-[13px] text-[#1E1B18]/80 hover:text-[#1E1B18] transition-colors font-medium">Therapist</button>
          <button onClick={() => scrollTo('booking')} className="text-[13px] text-[#1E1B18]/80 hover:text-[#1E1B18] transition-colors font-medium">Book</button>
          <button data-testid="nav-gift-cards" onClick={() => navigate('/gift-cards')} className="text-[13px] text-[#1E1B18]/80 hover:text-[#1E1B18] transition-colors font-medium">Gift Cards</button>

          {user ? (
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full border border-[#1E1B18]/15 hover:border-[#1E1B18]/40 transition-colors"
              title="Your account"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-[#E5DDF0] flex items-center justify-center">
                  <UserIcon size={14} className="text-[#7B6BA6]" />
                </div>
              )}
              <span className="text-[13px] font-medium text-[#1E1B18] max-w-[110px] truncate">
                {user.name?.split(' ')[0]}
              </span>
            </button>
          ) : (
            <button
              onClick={login}
              className="inline-flex items-center gap-1.5 text-[13px] text-[#1E1B18]/80 hover:text-[#1E1B18] font-medium transition-colors"
            >
              <LogIn size={14} /> Sign in
            </button>
          )}

          <button onClick={() => scrollTo('booking')} className="bg-[#0F0E0C] text-[#F4EFE7] px-5 py-2.5 rounded-full text-[13px] font-medium hover:bg-[#2A2622] transition-colors">
            Book Appointment
          </button>
        </nav>

        <div className="flex md:hidden items-center gap-2">
          <button data-testid="mobile-nav-gift-cards" onClick={() => navigate('/gift-cards')} className="text-[12px] font-medium text-[#1E1B18]/80 px-3 py-2">Gift Cards</button>
          <button data-testid="mobile-nav-book" onClick={() => scrollTo('booking')} className="bg-[#0F0E0C] text-[#F4EFE7] px-4 py-2 rounded-full text-[12px] font-medium">Book</button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
